import React from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Button, CircularProgress } from '@material-ui/core';
import apiURL from '../apiURL';

type Supplement = {
    id: string;
    status: string;
    contract_number?: string;
    contract_pdf_path?: string;
    contract_accepted?: boolean;
    contract_sent_at?: string;
    payment_url?: string;
    invoice_pdf_url?: string;
    invoice_hosted_url?: string;
};

const BoxSupplementActions = ({ supplement }: { supplement: Supplement }) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [diagnostic, setDiagnostic] = React.useState('');
    const [recoveryAvailable, setRecoveryAvailable] = React.useState(false);

    if (!record || !supplement?.id) return null;

    const perform = async (
        action: string,
        successMessage: string,
        confirmation?: string
    ) => {
        if (loading) return;
        if (confirmation && !window.confirm(confirmation)) return;

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('auth') || '';
            const response = await fetch(
                `${apiURL}/boxreservation/${encodeURIComponent(record.id)}` +
                `/supplements/${encodeURIComponent(supplement.id)}/${action}`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok)
                throw new Error(
                    result.message || `Erreur HTTP ${response.status}`
                );

            notify(successMessage, 'success');
            refresh();
        } catch (err) {
            const message = err instanceof Error
                ? err.message
                : 'Une erreur est survenue.';

            setError(message);
            notify(message, 'warning');
        } finally {
            setLoading(false);
        }
    };

    const diagnosePayment = async () => {
        if (loading) return;

        setLoading(true);
        setError('');
        setDiagnostic('');
        setRecoveryAvailable(false);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${encodeURIComponent(record.id)}` +
                `/supplements/${encodeURIComponent(supplement.id)}/recovery`,
                {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok)
                throw new Error(
                    result.message || `Erreur HTTP ${response.status}`
                );

            const labels: Record<string, string> = {
                open: 'Session retrouvée, paiement encore ouvert.',
                completed: 'Session terminée : rapprochement nécessaire.',
                not_found: 'Session introuvable : vérification manuelle nécessaire.',
                expired: 'Session expirée : renouvellement contrôlé nécessaire.',
            };

            setDiagnostic(
                labels[result.state] ||
                'État Stripe à vérifier : ' + (result.state || 'inconnu')
            );

            setRecoveryAvailable(
                result.state === 'open' &&
                result.action === 'session_recovery_available'
            );
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Diagnostic impossible.'
            );
        } finally {
            setLoading(false);
        }
    };

    const recoverPayment = async () => {
        if (loading || !recoveryAvailable) return;

        if (!window.confirm(
            'Rattacher la session Stripe ouverte à ce complément ?'
        )) return;

        setLoading(true);
        setError('');
        setRecoveryAvailable(false);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${encodeURIComponent(record.id)}` +
                `/supplements/${encodeURIComponent(supplement.id)}/recovery/open`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.recovered !== true)
                throw new Error(
                    result.message || 'Récupération impossible.'
                );

            setDiagnostic('Session Stripe récupérée.');
            notify('Session Stripe récupérée avec succès.', 'success');
            refresh();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Récupération impossible.'
            );
        } finally {
            setLoading(false);
        }
    };

    const buttonStyle = { marginRight: 8, marginTop: 8 };

    return (
        <div style={{ marginTop: 12 }}>
            {supplement.status === 'draft' ||
             supplement.status === 'contract_error' ? (
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={loading}
                    style={buttonStyle}
                    onClick={() => perform(
                        'contract',
                        'Contrat complémentaire généré.'
                    )}
                >
                    Générer le contrat
                </Button>
            ) : null}

            {supplement.status === 'contract_ready' &&
             !!supplement.contract_number &&
             !!supplement.contract_pdf_path &&
             !supplement.contract_accepted ? (
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={loading}
                    style={buttonStyle}
                    onClick={() => perform(
                        'prepare-acceptance',
                        'Acceptation du contrat préparée.'
                    )}
                >
                    Préparer l’acceptation
                </Button>
            ) : null}

            {supplement.status === 'awaiting_acceptance' &&
             !supplement.contract_accepted ? (
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    style={buttonStyle}
                    onClick={() => perform(
                        'send-contract',
                        'Contrat complémentaire envoyé.',
                        'Envoyer le nouveau contrat à la cliente ?'
                    )}
                >
                    {supplement.contract_sent_at
                        ? 'Renvoyer le contrat'
                        : 'Envoyer le contrat'}
                </Button>
            ) : null}

            {supplement.status === 'payment_pending' &&
             supplement.payment_url ? (
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={buttonStyle}
                    href={supplement.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Voir le paiement
                </Button>
            ) : null}

            {supplement.status === 'paid' &&
             (supplement.invoice_pdf_url ||
              supplement.invoice_hosted_url) ? (
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={buttonStyle}
                    component="a"
                    href={
                        supplement.invoice_pdf_url ||
                        supplement.invoice_hosted_url ||
                        ''
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Télécharger la facture
                </Button>
            ) : null}

            {[
                'creating_checkout',
                'checkout_recovery_required',
            ].includes(supplement.status) && (
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={loading}
                    style={buttonStyle}
                    onClick={diagnosePayment}
                >
                    Diagnostiquer le paiement
                </Button>
            )}

            {diagnostic && (
                <div style={{ marginTop: 8 }}>
                    {diagnostic}
                </div>
            )}
            {recoveryAvailable && (
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    style={buttonStyle}
                    onClick={recoverPayment}
                >
                    Récupérer la session Stripe
                </Button>
            )}



            {loading && (
                <CircularProgress size={18} style={{ marginLeft: 8 }} />
            )}

            {error && (
                <div style={{ color: '#c62828', marginTop: 8 }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default BoxSupplementActions;
